export interface AddressSegments {
  street: string
  city: string
  postalCode: string
  province: string
  country: string
}

export interface PlacePrediction {
  address?: string
  name?: string
  place_id?: string
  placeID?: string
  id?: string
  external_id?: string
}

export interface PlaceResult {
  address?: string
  name?: string
  street_number?: string
  locality?: string
  postal_code?: string
  admin_area_iso_code?: string
  placeId: string
  source?: string
  [key: string]: any
}

/**
 * Normalize a string for comparison (lowercase, remove extra spaces)
 */
export const normalizeString = (value?: string): string => {
  return value?.toLowerCase().replace(/\s+/g, ' ').trim() ?? ''
}

/**
 * Parse a full address string into segments
 * Example: "123 Main St, Montreal, H3A 1A1, QC, CA"
 */
export const parseAddressSegments = (fullAddress: string): AddressSegments => {
  const segments = fullAddress.split(',').map((segment) => segment.trim())
  return {
    street: segments[0] || '',
    city: segments[1] || '',
    postalCode: segments[2] || '',
    province: segments[3] || '',
    country: segments[4] || '',
  }
}

/**
 * Extract place ID from various possible field names
 */
export const extractPlaceId = (place?: PlacePrediction): string | undefined => {
  return place?.place_id || place?.placeID || place?.id || place?.external_id
}

/**
 * Build progressive search queries from most specific to least specific
 */
export const buildSearchQueries = (segments: AddressSegments, fullAddress: string): string[] => {
  const { street, city } = segments
  const queries = [street, `${street}, ${city}`, fullAddress]
    .map((entry) => entry.trim())
    .filter(Boolean)

  // Remove duplicates while preserving order
  return Array.from(new Set(queries))
}

/**
 * Create a manual place object when API search fails
 */
export const createManualPlaceFallback = (
  fullAddress: string,
  segments: AddressSegments
): PlaceResult => {
  const { street, city, postalCode, province, country } = segments

  return {
    address: fullAddress,
    name: street,
    street_number: street,
    locality: city,
    postal_code: postalCode,
    admin_area_iso_code: country && province ? `${country}-${province}` : province,
    placeId: fullAddress,
    source: 'prefilled_manual_parse',
  }
}

/**
 * Create a matcher function to find the best prediction from a list
 */
export const createPredictionMatcher = (
  fullAddress: string,
  city: string,
  postalCode: string
): ((predictions?: PlacePrediction[]) => PlacePrediction | undefined) => {
  const normalizedAddress = normalizeString(fullAddress)
  const normalizedCity = normalizeString(city)
  const normalizedPostal = normalizeString(postalCode)

  return (predictions: PlacePrediction[] = []): PlacePrediction | undefined => {
    return (
      predictions.find((prediction) => {
        const predictionAddress = normalizeString(prediction?.address)
        const predictionName = normalizeString(prediction?.name)

        const matchesAddress = predictionAddress && normalizedAddress?.includes(predictionAddress)
        const matchesCity =
          predictionAddress && normalizedCity && predictionAddress.includes(normalizedCity)
        const matchesPostal =
          predictionAddress && normalizedPostal && predictionAddress.includes(normalizedPostal)
        const matchesName = predictionName && normalizedAddress?.includes(predictionName)

        return matchesAddress || matchesCity || matchesPostal || matchesName
      }) || predictions[0]
    )
  }
}

/**
 * Search for a place using a single query
 */
export const searchPlaceByQuery = async (
  query: string,
  getPlacePredictions: (query: string, signal: AbortSignal) => Promise<PlacePrediction[]>,
  resolvePlace: (placeId: string) => Promise<any>,
  pickBestPrediction: (predictions?: PlacePrediction[]) => PlacePrediction | undefined
): Promise<PlaceResult | null> => {
  try {
    const abortController = new AbortController()
    const predictions = await getPlacePredictions(query, abortController.signal)
    const bestPrediction = pickBestPrediction(predictions)
    const placeId = extractPlaceId(bestPrediction)

    if (!placeId) return null

    const result = await resolvePlace(placeId)
    if (result) {
      return {
        ...result,
        placeId,
      }
    }
  } catch (_error) {}
  return null
}

/**
 * Search for an address using progressive fallback strategies
 */
export const searchAddress = async (
  fullAddress: string,
  getPlacePredictions: (query: string, signal: AbortSignal) => Promise<PlacePrediction[]>,
  resolvePlace: (placeId: string) => Promise<any>
): Promise<PlaceResult | null> => {
  if (!fullAddress) return null

  const segments = parseAddressSegments(fullAddress)
  const pickBestPrediction = createPredictionMatcher(
    fullAddress,
    segments.city,
    segments.postalCode
  )
  const queries = buildSearchQueries(segments, fullAddress)

  // Try each query progressively
  for (const query of queries) {
    const result = await searchPlaceByQuery(
      query,
      getPlacePredictions,
      resolvePlace,
      pickBestPrediction
    )

    if (result) {
      return {
        ...result,
        address: fullAddress, // Override with original full address
      }
    }
  }

  // Fallback: create manual place object
  return createManualPlaceFallback(fullAddress, segments)
}
